import { Analytics } from './apiClient/Analytics';
import { beUrl } from './Constants';
import { getAttribution } from './apiHelper/attribution';

// First-party, in-app analytics — replaces Google Analytics. Events are POSTed to our own
// backend (no third party, no cookies, no consent banner). A standalone generated client is
// used rather than ApiHelper, which calls React hooks in its constructor and so can't run
// from a plain function or effect.
const client = new Analytics({ baseUrl: beUrl });

const SESSION_KEY = "analyticsSession";

// Anonymous per-tab id, used only to count unique visitors. Random, first-party, and cleared
// when the tab closes — not a cookie, not cross-site, carries no personal data.
function sessionId(): string {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = Math.random().toString(36).slice(2) + Date.now().toString(36);
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return "nostore";
    }
}

// Fire-and-forget: analytics must never block or break the page. Only sends in production so
// local dev doesn't write into the (prod-defaulted) backend.
function send(type: string, fields: { path?: string; label?: string; detail?: string }) {
    if (process.env.NODE_ENV !== 'production') return;

    try {
        // All three UTM tags travel with the event so visits group by the same key the
        // sales and consultation reports use — that is what makes them comparable.
        const { utmSource, utmMedium, utmCampaign } = getAttribution();
        client.event({ type, sessionId: sessionId(), source: utmSource, medium: utmMedium, campaign: utmCampaign, ...fields })
            .catch(() => { /* best-effort */ });
    } catch { /* best-effort */ }
}

// A first-party pageview (replaces the GA pageview in RouteAnalytics).
export function trackPageview(path: string) {
    send("pageview", { path });
}

// Purchase/payment failures otherwise leave no trace: a react-alert toast vanishes and
// nothing is logged. console.error is the always-on client trail; the event adds an
// aggregate, queryable view in the admin statistics.
export function reportPaymentError(stage: string, detail?: unknown) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${stage}`, detail ?? "");
    send("payment", { label: stage, detail: detail == null ? undefined : String(detail) });
}
